import i18n from "../i18n";
import uploadWorkflowVideo from "../utils/upload-workflow-video";
import { youtubeParser } from "../utils/video-playlist";

const involveMeRegex = /(?:http:\/\/)?(?:([^.]+)\.)?involve\.me/;

const GettingStarted = (app) => {
  app
    .get(
      "/getting-started/publish/:id/:option",
      async ({ api, assert, res, req, flash }) => {
        const { id, option } = req.params;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );

        const body = {
          id,
          option,
          value: "published",
          key: "status",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t(
              "coaching-portal:gettingStartedActions.successfullyPublished"
            )
          );
          res.backDontScroll();
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .get(
      "/getting-started/unpublish/:id/:option",
      async ({ api, assert, res, req, flash }) => {
        const { id, option } = req.params;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );

        const body = {
          id,
          option,
          value: "draft",
          key: "status",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t(
              "coaching-portal:gettingStartedActions.successfullyUnpublished"
            )
          );
          res.backDontScroll();
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/video",
      async ({ api, assert, req, res, flash }) => {
        const { option, videoUrl, workflow: id, deleteVideo } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          videoUrl?.length || req.files?.videoFile,
          400,
          i18n.t("coaching-portal:gettingStartedActions.videoRequired")
        );

        const body = {
          id,
          option,
          key: "video",
        };
        if (videoUrl) {
          const isValidYoutube = youtubeParser(videoUrl);
          assert(
            isValidYoutube,
            400,
            i18n.t("coaching-portal:gettingStartedActions.validYoutubeRequired")
          );
          body.value = videoUrl;
        }
        if (deleteVideo) body.deleteVideo = deleteVideo;

        try {
          // The bytes go browser -> S3 directly; workflow-option only records the
          // reference, so no file is streamed through the API any more.
          if (req.files?.videoFile) {
            body.value = await uploadWorkflowVideo({
              api,
              body: req.body,
              files: req.files,
            });
          }

          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t(
              "coaching-portal:gettingStartedActions.successfullyAddedVideo"
            )
          );
          if (option === "welcomeVideo") {
            res.refreshWith(
              {
                "modal[type]": "",
              },
              { url: "back", hash: "#_" }
            );
          } else {
            res.refreshWith(
              {
                explanationAdded: "true",
              },
              { url: "back", hash: "#_" }
            );
          }
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/link-program",
      async ({ api, assert, req, res, flash }) => {
        const { option, url, workflow: id } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          url && involveMeRegex.test(url),
          400,
          i18n.t("coaching-portal:gettingStartedActions.validInvolveMeUrl")
        );

        const body = {
          id,
          option,
          key: "url",
          value: url,
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t(
              "coaching-portal:gettingStartedActions.successfullyLinkedProgram"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              explanationAdded: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/integrations",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );

        const items = Object.keys(req.body).filter(
          (key) => req.body[key] === true
        );

        const body = {
          id,
          option,
          value: items.length > 0 ? items : "empty",
          key: "items",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/select-macro-calculator",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id, value } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          value,
          400,
          i18n.t(
            "coaching-portal:gettingStartedActions.macroCalculatorRequired"
          )
        );

        const body = {
          id,
          option,
          value,
          key: "widget",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/select-video-playlist",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id, value } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          value,
          400,
          i18n.t("coaching-portal:gettingStartedActions.videoPlaylistRequired")
        );

        const body = {
          id,
          option,
          value,
          key: "playlist",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/select-guides",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id, value } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          value,
          400,
          i18n.t("coaching-portal:gettingStartedActions.guideRequired")
        );

        const body = {
          id,
          option,
          value,
          key: "widget",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/select-meal-plan-library",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id, value } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          value,
          400,
          i18n.t(
            "coaching-portal:gettingStartedActions.mealPlanLibraryRequired"
          )
        );

        const body = {
          id,
          option,
          value,
          key: "widget",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/select-daily-accountability-templates",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id, value } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          value,
          400,
          i18n.t(
            "coaching-portal:gettingStartedActions.dailyAccountabilityRequired"
          )
        );

        const body = {
          id,
          option,
          value,
          key: "widget",
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/add-contract",
      async ({ api, assert, req, res, flash }) => {
        const { option, contract, workflow: id } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );
        assert(
          contract,
          400,
          i18n.t("coaching-portal:gettingStartedActions.contractRequired")
        );

        const body = {
          id,
          option,
          key: "contract",
          value: contract,
        };

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t(
              "coaching-portal:gettingStartedActions.successfullyAddedContract"
            )
          );
          res.refreshWith(
            {
              "modal[type]": "",
              explanationAdded: "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/getting-started/questionnaire",
      async ({ api, assert, req, res, flash }) => {
        const { option, workflow: id, value } = req.body;
        assert(
          id,
          400,
          i18n.t("coaching-portal:gettingStartedActions.workflowIdRequired")
        );
        assert(
          option,
          400,
          i18n.t("coaching-portal:gettingStartedActions.optionRequired")
        );

        const body = {
          id,
          option,
          value,
          key: "questions",
        };

        console.log(body);

        try {
          await api("workflow-option", {
            method: "PUT",
            body,
          });
          flash(
            "success",
            i18n.t("coaching-portal:gettingStartedActions.successfullySaved")
          );
          res.refreshWith(
            {
              "modal[type]": "",
            },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    );
};

export default GettingStarted;
